import { useState, useEffect } from 'react'

const API = 'https://legado-digital-production.up.railway.app'

function Alerta({ userId, userEmail }) {
  const [alertaActiva, setAlertaActiva] = useState(false)
  const [estado, setEstado] = useState('active')
  const [ultimoCheckin, setUltimoCheckin] = useState(null)
  const [intervalo, setIntervalo] = useState(90)
  const [cargando, setCargando] = useState(true)
  const [guardando, setGuardando] = useState(false)
  const [mensaje, setMensaje] = useState('')

  useEffect(() => { cargarEstado() }, [userId])

  async function cargarEstado() {
    try {
      const res = await fetch(`${API}/api/switch/${userId}`)
      const datos = await res.json()
      if (datos.ok) {
        setAlertaActiva(datos.datos.switch_activo)
        setEstado(datos.datos.switch_estado)
        setUltimoCheckin(datos.datos.ultimo_checkin)
        setIntervalo(datos.datos.switch_intervalo_dias || 90)
      }
    } catch {
      console.error('Error cargando sistema de alerta')
    } finally {
      setCargando(false)
    }
  }

  async function handleActivar() {
    setGuardando(true)
    try {
      const res = await fetch(`${API}/api/switch/${userId}/activar`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ activo: !alertaActiva, intervalo_dias: intervalo }),
      })
      const datos = await res.json()
      if (datos.ok) {
        setAlertaActiva(!alertaActiva)
        setEstado('active')
        setMensaje(!alertaActiva ? 'Estoy bien activado.' : 'Estoy bien desactivado.')
        setTimeout(() => setMensaje(''), 4000)
      }
    } catch {
      setMensaje('Error al cambiar el estado')
    } finally {
      setGuardando(false)
    }
  }

  async function handleCheckin() {
    setGuardando(true)
    try {
      const res = await fetch(`${API}/api/switch/${userId}/checkin`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      })
      const datos = await res.json()
      if (datos.ok) {
        setEstado('active')
        setUltimoCheckin(new Date().toISOString())
        setMensaje('Confirmado. Todo en orden.')
        setTimeout(() => setMensaje(''), 4000)
      }
    } catch {
      setMensaje('Error al registrar la confirmación')
    } finally {
      setGuardando(false)
    }
  }

  async function handleCambiarIntervalo(nuevoIntervalo) {
    setIntervalo(nuevoIntervalo)
    if (!alertaActiva) return
    try {
      await fetch(`${API}/api/switch/${userId}/intervalo`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ intervalo_dias: nuevoIntervalo }),
      })
      setMensaje(`Frecuencia actualizada a ${nuevoIntervalo} días.`)
      setTimeout(() => setMensaje(''), 4000)
    } catch {
      setMensaje('Error al actualizar la frecuencia')
    }
  }

  function formatearFecha(fechaStr) {
    if (!fechaStr) return 'Nunca'
    return new Date(fechaStr).toLocaleDateString('es-ES', {
      day: 'numeric', month: 'long', year: 'numeric',
    })
  }

  function diasDesde(fechaStr) {
    if (!fechaStr) return 0
    return Math.floor((Date.now() - new Date(fechaStr).getTime()) / 86400000)
  }

  const estadoLabel = {
    active: 'Activo',
    warning_1: 'Primer aviso enviado',
    warning_2: 'Segundo aviso enviado',
    warning_3: 'Aviso final enviado',
    notified: 'Guardián notificado',
    confirmed: 'Confirmado por guardián',
    cooling_off: 'Periodo de espera',
    unlocking: 'Entregando información',
    completed: 'Información entregada',
  }

  const opciones = [
    { dias: 2, label: '2 días', descripcion: 'Para situaciones críticas o de salud delicada' },
    { dias: 7, label: '7 días', descripcion: 'Para situaciones de salud o riesgo elevado' },
    { dias: 30, label: '30 días', descripcion: 'Para personas con condiciones de salud' },
    { dias: 90, label: '90 días', descripcion: 'Estándar — para uso general' },
  ]

  if (cargando) return <p style={{ textAlign: 'center', color: '#8B6F47', padding: '24px' }}>Cargando...</p>

  return (
    <div style={estilos.pagina} className="pagina-contenido">
      <div style={estilos.contenido}>

        <div style={estilos.header}>
          <h2 style={estilos.titulo}>Estoy bien</h2>
          <p style={estilos.subtitulo}>
            Tu red de seguridad silenciosa. Cada cierto tiempo te enviamos una señal —
            solo confirmas que estás bien con un click. Si no respondes en el plazo
            que tú elijas, tu guardián recibe un aviso automático con tus datos críticos.
            Sin drama. Sin falsas alarmas. Tú tienes el control total.
          </p>
        </div>

        <div style={{
          backgroundColor: '#FDF0EC', border: '1px solid #F5C9B8',
          borderRadius: '12px', padding: '16px 20px', marginBottom: '24px',
          display: 'flex', gap: '12px', alignItems: 'flex-start'
        }}>
          <span style={{ fontSize: '20px', flexShrink: 0 }}>💡</span>
          <div style={{ fontSize: '13px', color: '#5C4033', lineHeight: '1.7' }}>
            <strong>¿Cómo funciona?</strong> Activa el sistema y elige cada cuánto tiempo
            quieres recibir la señal. Si no confirmas que estás bien, primero te enviamos
            recordatorios. Si sigues sin responder, tu guardián recibe un aviso con tus
            datos de emergencia. Tú puedes desactivarlo en cualquier momento.
          </div>
        </div>

        {mensaje && <div style={estilos.mensajeExito}>{mensaje}</div>}

        <div style={estilos.card}>
          <div style={estilos.cardTitulo}>¿Con qué frecuencia quieres confirmar que estás bien?</div>
          <p style={estilos.cardSubtitulo}>
            Elige según tu situación. Puedes cambiarlo en cualquier momento.
          </p>
          <div style={estilos.opcionesGrid}>
            {opciones.map(op => (
              <button
                key={op.dias}
                onClick={() => handleCambiarIntervalo(op.dias)}
                style={{
                  ...estilos.opcionBtn,
                  backgroundColor: intervalo === op.dias ? '#2C1810' : '#FAF8F5',
                  color: intervalo === op.dias ? '#FAF8F5' : '#2C1810',
                  border: intervalo === op.dias ? '2px solid #2C1810' : '1px solid #E8E0D5',
                }}
              >
                <div style={estilos.opcionLabel}>{op.label}</div>
                <div style={{ ...estilos.opcionDesc, color: intervalo === op.dias ? '#C4A882' : '#8B6F47' }}>
                  {op.descripcion}
                </div>
              </button>
            ))}
          </div>
        </div>

        <div style={estilos.card}>
          <div style={estilos.cardFila}>
            <div>
              <div style={estilos.cardTitulo}>Estado del sistema</div>
              <span style={{
                ...estilos.badge,
                backgroundColor: alertaActiva ? '#F0F4E8' : '#F5F0E8',
                color: alertaActiva ? '#3D4D1F' : '#8B6F47',
              }}>
                {alertaActiva ? estadoLabel[estado] : 'Desactivado'}
              </span>
            </div>
            <button
              onClick={handleActivar}
              disabled={guardando}
              style={{
                ...estilos.botonToggle,
                backgroundColor: alertaActiva ? '#8B2E0F' : '#2C1810',
                opacity: guardando ? 0.7 : 1,
              }}
            >
              {guardando ? 'Guardando...' : alertaActiva ? 'Desactivar' : 'Activar alerta'}
            </button>
          </div>
        </div>

        {alertaActiva && (
          <>
            <div style={estilos.card}>
              <div style={estilos.cardTitulo}>Última confirmación</div>
              <div style={estilos.checkinFecha}>{formatearFecha(ultimoCheckin)}</div>
              <div style={estilos.checkinDias}>Hace {diasDesde(ultimoCheckin)} días</div>
              <div style={estilos.barraContenedor}>
                <div style={estilos.barraLabel}>
                  <span>0 días</span>
                  <span>{intervalo} días</span>
                </div>
                <div style={estilos.barra}>
                  <div style={{
                    ...estilos.barraRelleno,
                    width: `${Math.min(diasDesde(ultimoCheckin) / intervalo * 100, 100)}%`,
                    backgroundColor: diasDesde(ultimoCheckin) > intervalo * 0.7 ? '#8B2E0F'
                      : diasDesde(ultimoCheckin) > intervalo * 0.4 ? '#8B6F47' : '#5C6B3A',
                  }} />
                </div>
              </div>
              <button
                onClick={handleCheckin}
                disabled={guardando}
                style={{ ...estilos.botonCheckin, opacity: guardando ? 0.7 : 1 }}
              >
                {guardando ? 'Confirmando...' : 'Confirmar que estoy bien'}
              </button>
            </div>

            <div style={estilos.card}>
              <div style={estilos.cardTitulo}>Cómo funciona</div>
              <div style={estilos.pasos}>
                {[
                  `Cada ${intervalo} días recibes una señal — confírmala para indicar que estás bien`,
                  'Si no respondes, tu guardián recibe un aviso',
                  'Si tu guardián confirma que necesitas ayuda, tu círculo recibe acceso a tu información',
                ].map((texto, i) => (
                  <div key={i} style={estilos.paso}>
                    <div style={estilos.pasoNum}>{i + 1}</div>
                    <div style={estilos.pasoTexto}>{texto}</div>
                  </div>
                ))}
              </div>
            </div>
          </>
        )}

      </div>
    </div>
  )
}

const estilos = {
  pagina: { minHeight: 'calc(100vh - 64px)', backgroundColor: '#FAF8F5', padding: '32px 16px', scrollMarginTop: '56px' },
  contenido: { maxWidth: '640px', margin: '0 auto' },
  header: { marginBottom: '28px' },
  titulo: { fontSize: '24px', fontWeight: '700', color: '#2C1810', marginBottom: '8px', letterSpacing: '-0.5px' },
  subtitulo: { fontSize: '14px', color: '#8B6F47', lineHeight: '1.7' },
  mensajeExito: { backgroundColor: '#F0F4E8', color: '#3D4D1F', padding: '12px 16px', borderRadius: '8px', fontSize: '14px', marginBottom: '16px', border: '1px solid #C8D5A0' },
  card: { backgroundColor: '#FFFFFF', borderRadius: '12px', padding: '22px 24px', border: '1px solid #E8E0D5', marginBottom: '14px' },
  cardTitulo: { fontSize: '14px', fontWeight: '600', color: '#2C1810', marginBottom: '8px' },
  cardSubtitulo: { fontSize: '13px', color: '#8B6F47', marginBottom: '16px', lineHeight: '1.5' },
  cardFila: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px' },
  opcionesGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' },
  opcionBtn: { padding: '14px', borderRadius: '10px', cursor: 'pointer', textAlign: 'left', transition: 'all 0.15s ease' },
  opcionLabel: { fontSize: '16px', fontWeight: '700', marginBottom: '4px' },
  opcionDesc: { fontSize: '11px', lineHeight: '1.4' },
  badge: { fontSize: '12px', padding: '4px 12px', borderRadius: '20px', fontWeight: '500', display: 'inline-block' },
  botonToggle: { color: '#FAF8F5', border: 'none', padding: '10px 22px', borderRadius: '8px', fontSize: '14px', fontWeight: '500', cursor: 'pointer', flexShrink: 0 },
  checkinFecha: { fontSize: '20px', fontWeight: '700', color: '#2C1810', marginBottom: '4px', letterSpacing: '-0.3px' },
  checkinDias: { fontSize: '13px', color: '#8B6F47', marginBottom: '18px' },
  barraContenedor: { marginBottom: '18px' },
  barraLabel: { display: 'flex', justifyContent: 'space-between', fontSize: '11px', color: '#C4A882', marginBottom: '6px' },
  barra: { height: '6px', backgroundColor: '#E8E0D5', borderRadius: '3px', overflow: 'hidden' },
  barraRelleno: { height: '6px', borderRadius: '3px', transition: 'width 0.3s ease' },
  botonCheckin: { width: '100%', padding: '13px', backgroundColor: '#5C6B3A', color: '#FAF8F5', border: 'none', borderRadius: '8px', fontSize: '14px', fontWeight: '500', cursor: 'pointer' },
  pasos: { display: 'flex', flexDirection: 'column', gap: '14px' },
  paso: { display: 'flex', gap: '12px', alignItems: 'flex-start' },
  pasoNum: { width: '26px', height: '26px', borderRadius: '50%', backgroundColor: '#2C1810', color: '#FAF8F5', fontSize: '12px', fontWeight: '600', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 },
  pasoTexto: { fontSize: '14px', color: '#5C4033', lineHeight: '1.6', paddingTop: '3px' },
}

export default Alerta